import { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { dashboardsContext } from 'src/lib/store/dashboardsContext';
import { Loader, Flex } from 'src/components';
import { useMutateSaveUserPreferences } from 'src/lib/hooks/api';
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  TreeView,
  TreeNode,
  Toggle,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import styles from './styles/preferencesModal.module.scss';
import _groupBy from 'lodash/groupBy';
import _startCase from 'lodash/startCase';
import _replace from 'lodash/replace';


export const PreferencesModal = ({ open, onRequestClose, existingPreferences }) => {
  const { dashboardsState } = useContext(dashboardsContext);
  const [ widgetOptions, setWidgetOptions ] = useState([]);
  const [ selectedNode, setSelectedNode ] = useState('');
  const [ widgetPreferences, setWidgetPreferences ] = useState({});
  const [ isLoading, setIsLoading ] = useState(false);
  const { mutate: mutateSavePreferences } = useMutateSaveUserPreferences();

  const availableWidgets = useMemo(() => {
    const keyedWidgets = [];
    dashboardsState?.widgets.forEach((widget) => {
      // If the widget does not have a dashboard, it should not appear in the preferences modal.
      if (widget?.dashboardId) {
        keyedWidgets.push({
          ...widget,
          widgetKey: `${ widget.name }:${ widget.dashboardId }`,
        });
      }
      return;
    });
    return keyedWidgets;
  }, [ dashboardsState?.widgets ]);

  const groupedWidgets = useMemo(() => _groupBy(availableWidgets, 'category'), [ availableWidgets ]);

  const generateWidgetPreference = useCallback(({ widget, isShown, widgetKey }) => {
    // If the widget key contains a colon, it means it's a key in the form of 'widgetName:dashboardId'. Here we extract the widget name and dashboard ID from the key.
    let widgetMeta = (widgetKey && widgetKey.includes(':')) ? {
      name: widgetKey.split(':')[0],
      dashboardId: widgetKey.split(':')[1],
      widgetKey: widgetKey,
    } : {};
    // If the widget's data object is provided (sometimes it's not), we use it to get the guaranteed widget name and dashboard ID.
    if (widget) {
      widgetMeta = {
        name: widget.name,
        dashboardId: widget.dashboardId,
        widgetKey: widget.widgetKey,
      };
    }
    return {
      ...widgetPreferences[widgetMeta.widgetKey || widget.name || widgetMeta.name || ''], // Do our best attempt to accurately retrieve existing preferences for this widget, despite that it may have changed, or be a duplicate.
      show: isShown,
      meta: widgetMeta,
    };
  }, [ widgetPreferences ]);

  // Set default widget preferences for each widget based on existing preferences then arbitrary defaults
  useEffect(() => {
    const existingWidgetsConfig = existingPreferences?.widget?.widgets;
    const widgetObjectDefaults = {};
    availableWidgets.forEach(widget => widgetObjectDefaults[widget.widgetKey] = generateWidgetPreference({ widget, isShown: false }));
    setWidgetPreferences({
      ...widgetObjectDefaults,
      ...(existingWidgetsConfig || {}),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ availableWidgets, existingPreferences ]);

  const onSubmit = () => {
    setIsLoading(true);
    mutateSavePreferences({
      widget: {
        widgets: widgetPreferences,
      },
    }, {
      onSuccess: () => {
        onRequestClose();
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  const onSelectedTreeNode = (_event, selectedNodeInner) => {
    setSelectedNode(selectedNodeInner?.value);
    setWidgetOptions(groupedWidgets[selectedNodeInner?.value]);
  };

  const onToggleWidget = (widgetSlug, enabled) => {
    setWidgetPreferences({
      ...widgetPreferences,
      [widgetSlug]: {
        ...widgetPreferences[widgetSlug],
        show: enabled,
      },
    });
  };

  const setAllWidgetsInCategory = (isShown) => {
    const newWidgetPreferences = {};
    widgetOptions.forEach((widget) => {
      newWidgetPreferences[`${ widget.name }:${ widget.dashboardId }`] = generateWidgetPreference({ widget, isShown });
    });
    setWidgetPreferences({
      ...widgetPreferences,
      ...newWidgetPreferences,
    });
  };

  const setAllWidgets = (isShown) => {
    const modifiedWidgetPreferences = {};
    Object.keys(widgetPreferences).forEach(widgetKey => modifiedWidgetPreferences[widgetKey] = generateWidgetPreference({ isShown, widgetKey }));
    setWidgetPreferences(modifiedWidgetPreferences);
  };

  const categoryOptions = Object.keys(groupedWidgets);

  return (
    <ComposedModal className={ styles.topModal } open={ open } size="lg" onClose={ onRequestClose }>
      <ModalBody className={ styles.modalContent } aria-label="Landing Page Preferences">
        <Flex style={{ minHeight: '70vh', height: '100%' }}>
          <Flex className={ styles.categoryNav } flexDirection="column">

            <Flex flexDirection="row" width="100%" justifyContent="space-between">
              <div className={ styles.categoryTitle }>
                <h3>Preferences</h3>
                <span>Set preferences for landing page</span>
              </div>
              <OverflowMenu
                data-floating-menu-container
                flipped
                align="right"
              >
                <OverflowMenuItem itemText="Enable All Widgets" onClick={ () => setAllWidgets(true) } />
                <OverflowMenuItem itemText="Disable All Widgets" onClick={ () => setAllWidgets(false) } />
              </OverflowMenu>
            </Flex>

            <div className={ styles.categoryTreeLabel }>Categories</div>
            <TreeView
              hideLabel
              className={ styles.categoryTreeView }
              label="Tree View"
              onSelect={ onSelectedTreeNode }
            >
              {categoryOptions.map(category => (
                <TreeNode
                  key={ category }
                  id={ category }
                  label={ <span>{ category }</span> }
                  value={ category }
                />
              ))}
            </TreeView>
          </Flex>
          <Flex flexDirection="column" width="100%" paddingTop="3em">
            <Flex flexDirection="column" justifyContent="flex-start" height="100%">
              <Flex flexDirection="row" width="100%" justifyContent="space-between" paddingRight="2em">
                <h5 style={{ marginTop: '0.5em' }}>Insights Available</h5>

                <OverflowMenu
                  data-floating-menu-container
                  flipped
                  align="left"
                >
                  <OverflowMenuItem itemText="Enable Widgets" onClick={ () => setAllWidgetsInCategory(true) } />
                  <OverflowMenuItem itemText="Disable Widgets" onClick={ () => setAllWidgetsInCategory(false) } />
                </OverflowMenu>
              </Flex>
              <div style={{ overflowY: 'auto', height: '100%' }}>
                {isLoading ? (
                  <Loader visible={ true } />
                ) : (
                  selectedNode ? (
                    widgetOptions.map((widget) => {
                      const visualizationTitle = widget?.visualization?.spec?.title?.text || '';
                      const widgetSlug = widget.widgetKey;
                      const cleanedWidgetName = _startCase(_replace(widget.name, /^widget_/g, ''));
                      const isToggled = !!widgetPreferences?.[widgetSlug]?.show;

                      return (
                        <>
                          <Flex
                            key={ widget.widgetKey }
                            justifyContent="space-between"
                            alignItems="center"
                            margin="1em 2em 0 1em"
                          >
                            <Flex flexDirection="column">
                              <div>{ cleanedWidgetName }</div>
                              <div className={ styles.widgetSlugName }>{ visualizationTitle }</div>
                            </Flex>
                            <div className={ styles.widgetToggle }>
                              <Toggle
                                hideLabel
                                aria-labelledby={ `Toggle ${ cleanedWidgetName }` }
                                toggled={ isToggled }
                                id={ widgetSlug }
                                onToggle={ enabled => onToggleWidget(widgetSlug, enabled) }
                              />
                            </div>
                          </Flex>
                        </>
                      );
                    })
                  ) : (
                    <Flex
                      className={ styles.widgetsPlaceholderText }
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <h3>Please select a category</h3>
                    </Flex>
                  )
                )}
              </div>
            </Flex>
          </Flex>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          onClick={ onRequestClose }
        >
          <div>Cancel</div>
        </Button>
        <Button
          kind="primary"
          onClick={ onSubmit }
        >
          <div>Save</div>
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};